import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Check, Facebook, Link2, Mail, MessageCircle, MessageSquare, Share2, Twitter } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { canNativeShare, copyText, shareNatively, shareTargets } from "@/lib/share";
import { qrImageUrl } from "@/lib/qr";

// One share surface for the whole site: the phone's own share sheet first, then
// the destinations people actually use, then copy-link.
const TILE = {
  whatsapp: "bg-gradient-to-br from-[#35e879] to-[#16a34a] text-white shadow-[0_6px_16px_rgba(37,211,102,0.22)]",
  facebook: "bg-gradient-to-br from-[#4295ff] to-[#1664d8] text-white shadow-[0_6px_16px_rgba(24,119,242,0.2)]",
  x: "bg-gradient-to-br from-[#363636] to-[#080808] text-white shadow-[0_6px_16px_rgba(0,0,0,0.18)]",
  sms: "bg-gradient-to-br from-[#38bdf8] to-[#0284c7] text-white shadow-[0_6px_16px_rgba(14,165,233,0.2)]",
  email: "bg-gradient-to-br from-[#fb923c] to-[#ea580c] text-white shadow-[0_6px_16px_rgba(249,115,22,0.2)]",
};

const ICON = {
  whatsapp: MessageCircle,
  facebook: Facebook,
  x: Twitter,
  sms: MessageSquare,
  email: Mail,
};

export default function ShareSheet({
  open,
  onOpenChange,
  title,
  text,
  url,
  heading = undefined,
  copyValue = undefined,
  copyLabelKey = undefined,
  copiedLabelKey = undefined,
}) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const targets = shareTargets({ title, text, url });
  const native = canNativeShare();

  const copyLink = async () => {
    // A list is worth more than its link, so when one is sharing it is the whole
    // message that goes to the clipboard — ready to paste into a chat.
    const ok = await copyText(copyValue || url);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-28px)] max-w-[460px] gap-0 overflow-hidden rounded-[28px] border border-black/[0.06] bg-[#fbfaf7] p-0 shadow-[0_28px_90px_rgba(0,0,0,0.24)] dark:border-white/[0.1] dark:bg-[#1b1916]">
        <div className="relative overflow-hidden border-b border-black/[0.07] px-6 pb-5 pt-6 dark:border-white/[0.1]"><div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#e52b3f] via-white to-[#3c82e8]" />
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-[#d93632]/15 bg-[#d93632]/[0.07] px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-[#c62e2a]"><Share2 className="h-3 w-3" /> Ya Tú Sabes PR</div><DialogTitle className="text-[22px] font-black tracking-[-0.045em] text-[#191917] dark:text-[#f7f5ef]">{heading || t("share.title")}</DialogTitle>
          <DialogDescription className="mt-1.5 text-[13px] leading-5 text-[#77736c] dark:text-[#b8b3a9]">
            {t("share.subtitle")}
          </DialogDescription>
        </div>

        <div className="px-6 py-5">
          {native && (
            <button
              type="button"
              onClick={() => shareNatively({ title, text, url })}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-[#d93632] bg-gradient-to-b from-[#f24a40] to-[#c91f2c] px-4 py-3 text-sm font-bold tracking-[-0.01em] text-white shadow-[0_8px_20px_rgba(217,54,50,0.22),inset_0_1px_0_rgba(255,255,255,0.38)] transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d93632] focus-visible:ring-offset-2"
            >
              <Share2 className="w-4 h-4" /> {t("share.native")}
            </button>
          )}

          <div className="mt-5 grid grid-cols-5 gap-2.5">
            {targets.map(({ key, label, href }) => {
              const Icon = ICON[key];
              return (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex min-w-0 flex-col items-center gap-2 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d93632] focus-visible:ring-offset-2 rounded-xl"
                >
                  <span className={`grid h-12 w-12 place-items-center rounded-[17px] ring-1 ring-black/[0.04] transition duration-200 group-hover:-translate-y-0.5 group-hover:shadow-lg group-active:scale-90 sm:h-[52px] sm:w-[52px] ${TILE[key]}`}>
                    <Icon className="w-[19px] h-[19px]" />
                  </span>
                  <span className="text-[10px] font-bold tracking-[-0.01em] text-[#625f58] dark:text-[#d0cbc1]">
                    {label}
                  </span>
                </a>
              );
            })}
          </div>

          <button
            type="button"
            onClick={copyLink}
            className="mt-5 flex min-h-[52px] w-full items-center gap-3 rounded-2xl border border-black/[0.09] bg-white/80 px-4 py-3 text-left shadow-[0_2px_8px_rgba(20,18,14,0.035)] transition hover:border-[#d93632]/40 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d93632] dark:border-white/[0.12] dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
          >
            {copied ? <Check className="w-4 h-4 shrink-0 text-[#F97316]" /> : <Link2 className="w-4 h-4 shrink-0" />}
            <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-[#6d6d6a]">{url}</span>
            <span className="shrink-0 font-mono text-[9.5px] tracking-[0.14em] uppercase font-bold">
              {copied ? t(copiedLabelKey || "share.copied") : t(copyLabelKey || "share.copy")}
            </span>
          </button>

          {/* Face to face is still the best share: one phone holds up the code,
              the other scans it, and the place is on both. */}
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-black/[0.09] bg-white/80 p-3 dark:border-white/[0.12] dark:bg-white/[0.04]">
            <img
              src={qrImageUrl(url, 220)}
              alt=""
              width={70}
              height={70}
              loading="lazy"
              className="h-[70px] w-[70px] shrink-0 rounded-lg border border-black/[0.08] bg-white object-contain"
            />
            <p className="text-[11px] font-medium leading-[1.45] text-[#625f58] dark:text-[#d0cbc1]">{t("share.scan")}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}